import {
  useContext, useEffect, useMemo, useState,
} from 'react'

import { faHouse, faStore } from '@fortawesome/pro-light-svg-icons'

import { useSetState } from '@domain-group/react-hooks'

import { matchFilterString } from '@domain-group/javascript-utils'

import {
  Button, Form,
  InputSearch,
  ModalContext,
  SectionDivider,
  SegmentedControl,
  SidebarModal,
  Text,
} from '@domain-group/suit-theme'

import useBrandCustomProfiles from '@hooks/useBrandCustomProfiles'
import useFlipperKey from '@hooks/useFlipperKey'
import useSelector from '@hooks/useSelector'
import useUser, { useUserForm } from '@hooks/useUser'

import { getCampaignPermissions } from '@functions/campaign'
import { toggleNestedValue } from '@functions/entity'
import type { HandleCallbackActionParams } from '@functions/handleCallbackAction'

import ToggleListItem from '@components/ToggleListItem'

import type { BrandCustomProfileModel } from '@models/types'
import type { PermissionItem } from '@models/brandCustomProfile'

import PermissionGroup from './components/PermissionGroup'

const MODAL_KEY = 'ManageUserPermissionsModal'

function mapPermissionDataToCustomPermissions(
  selectedBrandCustomProfile: BrandCustomProfileModel,
) {
  const { profile_type, permission_data } = selectedBrandCustomProfile

  const mappedPermissions = Object.fromEntries(
    Object.entries(permission_data || {}).map(([group, perms]) => [
      group,
      Object.fromEntries(
        (perms as PermissionItem[]).map((perm: PermissionItem) => [perm.key, perm.is_enabled]),
      ),
    ]),
  )

  return {
    [profile_type]: mappedPermissions,
    // example return => { agency_marketing: { group1: { perm1: true, perm2: false }} }
  }
}

const getPermissionGroupConfiguration = (
  permissionGroupKey: string,
  selectedBrandCustomProfiles: { [key: string]: string },
  filterKey: 'agency_marketing' | 'property_marketing',
  agencyMarketingPermissionsEnabled: boolean,
  selectedBrandCustomProfile: BrandCustomProfileModel,
  custom_permissions: any,
  enable_brand_custom_profiles?: boolean,
) => {
  let customPermissionGroup
  let updateString
  let disabled = false

  const allTogglesEnabled = selectedBrandCustomProfiles?.[filterKey] === 'system_default'
    && agencyMarketingPermissionsEnabled
    && enable_brand_custom_profiles

  let selectedBrandProfilePermissions: { [key: string]: Array<{ key: string, is_enabled: boolean }> } = {}
  if (selectedBrandCustomProfile){
    selectedBrandProfilePermissions = selectedBrandCustomProfile.permission_data || {}
  }

  if (agencyMarketingPermissionsEnabled && enable_brand_custom_profiles){
    // Map the array of permissions to an object: { [key]: is_enabled }
    const brandProfileGroupArray = selectedBrandProfilePermissions[permissionGroupKey] || []
    customPermissionGroup = Object.fromEntries(
      brandProfileGroupArray.map((perm: { key: string, is_enabled: boolean }) => [perm.key, perm.is_enabled]),
    )

    if (!selectedBrandCustomProfiles && enable_brand_custom_profiles){
      return null
    }
    disabled = true
    updateString = '' // Not used when disabled
  } else if (agencyMarketingPermissionsEnabled){
    customPermissionGroup = custom_permissions?.agency?.campaign?.[filterKey]?.[permissionGroupKey] || {}
    updateString = `custom_permissions.agency.campaign.${filterKey}.${permissionGroupKey}`
  } else {
    customPermissionGroup = custom_permissions?.agency?.campaign?.[permissionGroupKey] || {}
    updateString = `custom_permissions.agency.campaign.${permissionGroupKey}`
  }

  return {
    customPermissionGroup,
    updateString,
    disabled,
    allTogglesEnabled,
  }
}

type DefaultStateParams = {
  filterString: string,
  brand_custom_profile_settings_enabled: boolean,
  filterKey: 'agency_marketing' | 'property_marketing',
  selectedBrandCustomProfile: BrandCustomProfileModel | null,
  campaignCustomizablePermissions: { [key: string]: any },
}

const defaultState = {
  filterString: '',
  brand_custom_profile_settings_enabled: false,
  filterKey: 'property_marketing' as 'agency_marketing' | 'property_marketing',
  selectedBrandCustomProfile: null,
  campaignCustomizablePermissions: {},
}

export type ModalCallbacks = {
  closeModal: () => void,
  updateUser: (payload: HandleCallbackActionParams) => void,
}

type ModalProps = {
  callbacks: ModalCallbacks,
  showModal: boolean,
}

const ManageUserPermissionsModal = (props: ModalProps) => {
  const { callbacks, showModal } = props
  const { closeModal, updateUser } = callbacks || {}

  const modalContext = useContext(ModalContext)
  const { modalData } = modalContext

  const modalPayload = modalData?.[MODAL_KEY] || {}
  const { user: initUser } = modalPayload

  const [state, setState] = useSetState<DefaultStateParams>(defaultState)
  const {
    campaignCustomizablePermissions, filterString, filterKey,
  } = state

  const {
    callbacks: { updateUser: updateFn },
    user,
    updating,
  } = useUser(initUser, {
    performHttpRequests: true,
    requestOptions: {
      include_brand_custom_profile_user_profile_ids: true,
      include_user_frontend_permissions: true,
    },
  })

  const userFormPayload = useUserForm(user, { validateOn: 'change' })
  const {
    entityState,
    entityState: {
      brand_custom_profile_user_profile_ids, custom_permissions,
      enable_brand_custom_profiles, enable_custom_permissions,
    },
    setEntityState,
  } = userFormPayload

  const baseBrandCustomProfiles = useMemo(() => {
    if (!brand_custom_profile_user_profile_ids){
      return {
        property_marketing: 'system_default',
        agency_marketing: 'system_default',
      }
    }

    return {
      property_marketing: brand_custom_profile_user_profile_ids.property_marketing
        ? String(brand_custom_profile_user_profile_ids.property_marketing)
        : 'system_default',
      agency_marketing: brand_custom_profile_user_profile_ids.agency_marketing
        ? String(brand_custom_profile_user_profile_ids.agency_marketing)
        : 'system_default',
    }
  }, [brand_custom_profile_user_profile_ids])

  // State for user modifications (what the user selects in the dropdown)
  const [userModifications, setUserModifications] = useState<{ [key: string]: string }>({})

  // Merge base values with user modifications to get the current selected profiles
  const selectedBrandCustomProfiles = useMemo(() => ({
    ...baseBrandCustomProfiles,
    ...userModifications,
  }), [baseBrandCustomProfiles, userModifications])

  const adminCustomizablePermissions = user?.customizable_permissions?.agency?.admin || {}
  const adminPermissionGroupKeys = Object.keys(adminCustomizablePermissions)

  useEffect(() => {
    if (filterKey){
      const campaignPermissions = getCampaignPermissions({
        user,
        campaignType: filterKey,
      })

      setState({ campaignCustomizablePermissions: campaignPermissions })
    }
  }, [filterKey, user])

  const campaignPermissionGroupKeys = Object.keys(campaignCustomizablePermissions)

  const filteredAdminPermissionGroupKeys = adminPermissionGroupKeys
    .filter((key: string) => matchFilterString(key, filterString))

  const filteredCampaignPermissionGroupKeys = campaignPermissionGroupKeys
    .filter((key: string) => matchFilterString(key, filterString))

  const updateUserPayload = {
    callbacks: {
      action: updateFn,
      afterAction: () => {
        closeModal()
      },
    },
    entityParams: {
      ...entityState,
      brand_custom_profile_params: enable_brand_custom_profiles ? {
        agency_marketing: selectedBrandCustomProfiles?.agency_marketing,
        property_marketing: selectedBrandCustomProfiles?.property_marketing,
      } : undefined,
    },
    requestOptions: { include_brand_custom_profile_user_profile_ids: true },
    toastText: 'User Permissions Updated',
  }

  const { isEnabled: agencyMarketingPermissionsEnabled } = useFlipperKey('agency_marketing_permissions')

  const brandCustomProfilesPayload = useBrandCustomProfiles({
    filters: {
      brand_id: user?.brand_id,
      profile_type: filterKey,
      role_id: user?.role_id,
    },
    performHttpRequests: true,
  })
  const { filteredBrandCustomProfiles } = brandCustomProfilesPayload

  const brandCustomProfiles = useSelector(reduxState => reduxState.entities.brandCustomProfiles)

  const selectedBrandCustomProfile = useMemo(
    () => brandCustomProfiles[selectedBrandCustomProfiles?.[filterKey]] || 'system_default',
    [brandCustomProfiles, selectedBrandCustomProfiles, filterKey],
  )

  return (
    <SidebarModal callbacks={callbacks} clickSafeZone modalKey={MODAL_KEY} showModal={showModal}>
      <SidebarModal.Header callbacks={callbacks} title="Manage" titleSecondLine="Permissions" />

      <SidebarModal.Content>
        {agencyMarketingPermissionsEnabled && (
          <ToggleListItem
            onClick={() => {
              setEntityState({
                enable_brand_custom_profiles: !enable_brand_custom_profiles,
                enable_custom_permissions: true,
              })
            }}
            toggleValue={enable_brand_custom_profiles || false}
            toggleOnText="Yes"
            toggleOffText="No"
          >
            <Text
              color="bodyFontLightColor"
              fontSize="small"
              maxWidth="fit-content"
              variant="twoLineEllipsis"
              paddingRight="large"
            >
              Use Brand Profile
            </Text>
          </ToggleListItem>
        )}

        <ToggleListItem
          onClick={() => setEntityState({
            enable_custom_permissions: !enable_custom_permissions,
            enable_brand_custom_profiles: enable_custom_permissions ? false : enable_brand_custom_profiles,
          })}
          toggleValue={!enable_custom_permissions}
          toggleOnText="Default"
          toggleOffText="Custom"
        >
          <Text
            color="bodyFontLightColor"
            fontSize="small"
            maxWidth="fit-content"
            variant="twoLineEllipsis"
            paddingRight="large"
          >
            {enable_custom_permissions
              ? 'User has custom management permissions'
              : 'User has full management permissions'}
          </Text>
        </ToggleListItem>

        {(enable_custom_permissions || enable_brand_custom_profiles) && (
          <>
            <Form.Field label="Filter Permissions">
              {/* @ts-expect-error TS2741 */}
              <InputSearch
            // @ts-expect-error TS7006
                onChange={e => setState({ filterString: e.target.value })}
                value={filterString}
              />
            </Form.Field>

            <SectionDivider marginTop="large">Admin</SectionDivider>
            {(filterString
              ? filteredAdminPermissionGroupKeys
              : adminPermissionGroupKeys).map((permissionGroupKey, index, arr) => {
              const permissionGroup = adminCustomizablePermissions[permissionGroupKey]
              const customPermissionGroup = custom_permissions?.agency?.admin?.[permissionGroupKey] || {}

              return (
                <PermissionGroup
                  key={permissionGroupKey}
                  callbacks={{
                    updatePermissionGroup: (permissionKey: string) => toggleNestedValue(
                      userFormPayload,
                      `custom_permissions.agency.admin.${permissionGroupKey}.${permissionKey}`,
                      { shouldSetState: true },
                    ),
                  }}
                  customPermissionGroup={customPermissionGroup}
                  lastGroup={arr.length === index + 1}
                  permissionGroup={permissionGroup}
                  permissionGroupKey={permissionGroupKey}
                />
              )
            })}

            <SectionDivider>Campaign</SectionDivider>

            {agencyMarketingPermissionsEnabled && (
              <>
                <SegmentedControl
                  activeButtonKey={filterKey}
                  boxProps={{
                    justifyContent: 'center',
                  }}
                  buttons={[
                  // @ts-expect-error TS2322
                    { key: 'property_marketing', icon: faHouse, label: 'Property' },
                    // @ts-expect-error TS2322
                    { key: 'agency_marketing', icon: faStore, label: 'Agency' },
                  ]}
                  // @ts-expect-error TS2322
                  onClick={key => setState({ filterKey: key })}
                />

                {enable_brand_custom_profiles && (
                  <Form.Field
                    key={filterKey}
                    label={`Brand Profile for ${filterKey.replace('_', ' ')}`}
                    marginTop="large"
                  >
                    <select
                      value={selectedBrandCustomProfile?.id || 'system_default'}
                      onChange={(e) => {
                        const selectedId = e.target.value

                        setUserModifications({
                          ...userModifications,
                          [filterKey]: selectedId,
                        })

                        if (selectedId === 'system_default'){
                          // Set all permissions to true for this campaign type
                          const allTruePermissions = Object.fromEntries(
                            Object.entries(campaignCustomizablePermissions).map(([group, perms]) => [
                              group,
                              Object.fromEntries(
                                Object.keys(perms as object).map(permKey => [permKey, true]),
                              ),
                            ]),
                          )
                          setEntityState({
                            custom_permissions: {
                              ...entityState.custom_permissions,
                              agency: {
                                ...entityState.custom_permissions?.agency,
                                brand_custom_profile: {
                                  ...entityState.custom_permissions?.agency?.brand_custom_profile,
                                  [filterKey]: allTruePermissions,
                                },
                              },
                            },
                          })

                          return
                        }

                        const selectedProfile = filteredBrandCustomProfiles
                          .find(profile => String(profile.id) === selectedId)

                        if (selectedProfile){
                          const mappedPermissions = mapPermissionDataToCustomPermissions(selectedProfile)
                          setEntityState({
                            custom_permissions: {
                              ...entityState.custom_permissions,
                              agency: {
                                ...entityState.custom_permissions?.agency,
                                brand_custom_profile: {
                                  ...entityState.custom_permissions?.agency?.brand_custom_profile,
                                  ...mappedPermissions,
                                },
                              },
                            },
                          })
                        }
                      }}
                    >
                      <option value="system_default">System Defaults</option>
                      {filteredBrandCustomProfiles
                        .filter(profile => profile.profile_type === filterKey)
                        .map(profile => (
                          <option key={profile.id} value={profile.id}>
                            {profile.title}
                          </option>
                        ))}
                    </select>
                  </Form.Field>
                )}
              </>
            )}

            {(filterKey ? filteredCampaignPermissionGroupKeys
              : campaignPermissionGroupKeys).map((permissionGroupKey, index, arr) => {
              const permissionGroup = campaignCustomizablePermissions[permissionGroupKey]

              const config = getPermissionGroupConfiguration(
                permissionGroupKey,
                selectedBrandCustomProfiles,
                filterKey,
                agencyMarketingPermissionsEnabled,
                selectedBrandCustomProfile,
                custom_permissions,
                enable_brand_custom_profiles,
              )

              if (!config) return null

              const {
                customPermissionGroup, updateString, disabled, allTogglesEnabled,
              } = config

              return (
                <PermissionGroup
                  allEnabled={allTogglesEnabled}
                  key={permissionGroupKey}
                  callbacks={{
                    updatePermissionGroup: (permissionKey: string) => {
                      if (!disabled){
                        toggleNestedValue(
                          userFormPayload,
                          `${updateString}.${permissionKey}`,
                          { shouldSetState: true },
                        )
                      }
                    },
                  }}
                  customPermissionGroup={customPermissionGroup}
                  lastGroup={arr.length === index + 1}
                  permissionGroup={permissionGroup}
                  permissionGroupKey={permissionGroupKey}
                  disabled={disabled}
                />
              )
            })}
          </>
        )}
      </SidebarModal.Content>

      <SidebarModal.Footer>
        <Button
          buttonStyle="primaryCreate"
          loading={updating}
          onClick={() => updateUser(updateUserPayload)}
          size="large"
        >
          Save
        </Button>
      </SidebarModal.Footer>
    </SidebarModal>
  )
}

const LazyLoadedModal = (props: ModalProps) => (
  <SidebarModal.RenderController {...props}>
    <ManageUserPermissionsModal {...props} />
  </SidebarModal.RenderController>
)

export default LazyLoadedModal
